import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "summary-page" }
const _hoisted_2 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_3 = {
  key: 1,
  class: "detail-content"
}
const _hoisted_4 = { class: "info-tags-container" }
const _hoisted_5 = { class: "info-tags" }
const _hoisted_6 = { class: "tag-value" }
const _hoisted_7 = { class: "tag-value" }
const _hoisted_8 = {
  key: 0,
  class: "projects-section"
}
const _hoisted_9 = {
  key: 0,
  class: "special-attributes"
}
const _hoisted_10 = { class: "special-attributes-list" }
const _hoisted_11 = { class: "attribute-label" }
const _hoisted_12 = { class: "attribute-value" }
const _hoisted_13 = {
  key: 1,
  class: "detailed-data"
}
const _hoisted_14 = { class: "table-summary-header" }
const _hoisted_15 = { class: "table-title" }
const _hoisted_16 = { class: "table-summary" }
const _hoisted_17 = {
  key: 0,
  class: "option-example-images"
}
const _hoisted_18 = {
  key: 0,
  class: "option-example-placeholder"
}
const _hoisted_19 = {
  key: 1,
  class: "option-example-placeholder"
}
const _hoisted_20 = { class: "option-example-placeholder" }
const _hoisted_21 = { class: "option-example-placeholder" }
const _hoisted_22 = {
  key: 0,
  class: "main-table-images"
}
const _hoisted_23 = { class: "images-grid-small" }
const _hoisted_24 = {
  key: 0,
  class: "image-error-placeholder"
}
const _hoisted_25 = {
  key: 1,
  class: "image-error-placeholder"
}
const _hoisted_26 = { class: "image-error-small" }
const _hoisted_27 = { class: "image-loading-small" }
const _hoisted_28 = { class: "remark-image-preview-dialog" }
const _hoisted_29 = { class: "remark-image-preview-stage" }
const _hoisted_30 = ["src"]
const _hoisted_31 = { class: "remark-image-preview-note" }
const _hoisted_32 = { class: "remark-image-preview-note-value" }

import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight, Picture, Loading, Download } from '@element-plus/icons-vue'
import PageHeader from '@/components/PageHeader.vue'
import { useDetailStore } from '@/store/detailStore'
import { useRouteTitle } from '@/utils/useRouteTitle'
import ParameterSettings from '@/network/apis/ParameterSettings'
import { list } from '@/network/index'

interface DetailImageItem {
  fileName: string
  url: string
  remark?: string
  loading?: boolean
  error?: boolean
}

interface RemarkImageSource {
  fileName: string
  remark: string
}

// 从 store 获取经营项目信息

export default /*@__PURE__*/_defineComponent({
  __name: 'summary',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const detailStore = useDetailStore()
const pageTitle = useRouteTitle('原始数据汇总')

const loading = ref(false)
const downloadingImages = ref(false)
const summaryData = ref<Record<string, unknown> | null>(null)

const businessInfo = computed(() => detailStore.getSummaryBusinessInfo())

// 备注图像列表（按主表ID分组）
const remarkImagesByMainTable = ref<Map<string, DetailImageItem[]>>(new Map())
const optionExampleImagesByMainTable = ref<Map<string, DetailImageItem[]>>(new Map())
// 图片请求在页面显示后仍可能继续执行，下载图片包必须等待这组任务结束。
let remarkImagesLoadingPromise: Promise<void> = Promise.resolve()
const remarkImagePreviewVisible = ref(false)
const remarkPreviewImages = ref<DetailImageItem[]>([])
const remarkPreviewIndex = ref(0)
const activeRemarkPreviewImage = computed(() => remarkPreviewImages.value[remarkPreviewIndex.value])

// 获取指定主表的图片列表
const getMainTableImages = (mainData: any) => {
  const mainTableId = mainData.Id || mainData.id
  return remarkImagesByMainTable.value.get(String(mainTableId)) || []
}

const openRemarkImagePreview = (mainData: any, imageIndex: number) => {
  const sourceImages = getMainTableImages(mainData)
  const targetImage = sourceImages[imageIndex]
  const previewImages = sourceImages.filter(image => !image.loading && !image.error && image.url)
  const previewIndex = previewImages.findIndex(image => image === targetImage)
  if (previewIndex < 0) return

  remarkPreviewImages.value = previewImages
  remarkPreviewIndex.value = previewIndex
  remarkImagePreviewVisible.value = true
}

const changeRemarkPreviewImage = (offset: number) => {
  const nextIndex = remarkPreviewIndex.value + offset
  if (nextIndex >= 0 && nextIndex < remarkPreviewImages.value.length) {
    remarkPreviewIndex.value = nextIndex
  }
}

const getOptionExampleImages = (mainData: any) => {
  const mainTableId = mainData.Id || mainData.id
  return optionExampleImagesByMainTable.value.get(String(mainTableId)) || []
}

// 获取转换后的标题（处理单位替换）
const getTransformedTitle = (title: string, project: any, mainData: any): string => {
  if (!title) return ''
  
  // 检查是否有单位替换配置
  const unitReplaceJSON = project.检测原始数据检测数据格式描述?.单位替换JSON
  if (!unitReplaceJSON) return title
  
  try {
    const unitReplaceMap = JSON.parse(unitReplaceJSON)
    const summary = mainData.显示摘要 || ''
    
    // 遍历替换规则
    for (const [key, value] of Object.entries(unitReplaceMap)) {
      if (summary.includes(key)) {
        return title.replace(/\([^)]*\)/, `(${value})`)
      }
    }
  } catch (error) {
    console.error('解析单位替换JSON失败:', error)
  }
  
  return title
}

// 判断是否有分隔线
const hasDividerLine = (project: any, columnIndex: number): boolean => {
  const dividerLines = project.检测原始数据检测数据格式描述?.分区线
  if (!dividerLines || !Array.isArray(dividerLines)) return false
  return dividerLines[columnIndex] === true
}

const toNonEmptyString = (value: unknown): string => {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

const normalizeRemarkImage = (source: unknown): RemarkImageSource | null => {
  if (typeof source === 'string') {
    const fileName = toNonEmptyString(source)
    return fileName ? { fileName, remark: '' } : null
  }

  if (Array.isArray(source)) {
    const fileName = toNonEmptyString(source[0])
    return fileName ? { fileName, remark: toNonEmptyString(source[1]) } : null
  }

  if (!source || typeof source !== 'object') return null
  const item = source as Record<string, unknown>
  const fileName = toNonEmptyString(
    item['文件名'] ?? item['fileName'] ?? item['名称'] ?? item['name'] ?? item['图片名称'] ?? item['图片']
  )
  if (!fileName) return null
  return {
    fileName,
    remark: toNonEmptyString(item['备注'] ?? item['remark'] ?? item['说明'] ?? item['内容'])
  }
}

const parseRemarkImages = (source: unknown): RemarkImageSource[] => {
  if (!Array.isArray(source)) return []
  return source.map(normalizeRemarkImage).filter((item): item is RemarkImageSource => item !== null)
}

const parseDownloadResponseToUrl = (res: any): string => {
  if (res instanceof Blob) {
    return URL.createObjectURL(res)
  }

  const data = res?.response ?? res?.data ?? res
  if (data instanceof Blob) {
    return URL.createObjectURL(data)
  }

  if (typeof data === 'string') {
    if (data.startsWith('blob:') || data.startsWith('data:') || data.startsWith('http')) {
      return data
    }
    return `data:image/jpeg;base64,${data}`
  }

  return ''
}

const revokeImageMapUrls = (imageMap: Map<string, DetailImageItem[]>) => {
  for (const images of imageMap.values()) {
    for (const image of images) {
      if (image.url?.startsWith('blob:')) {
        URL.revokeObjectURL(image.url)
      }
    }
  }
}

const clearRemarkImages = () => {
  revokeImageMapUrls(remarkImagesByMainTable.value)
  remarkImagesByMainTable.value = new Map()
}

const clearOptionExampleImages = () => {
  revokeImageMapUrls(optionExampleImagesByMainTable.value)
  optionExampleImagesByMainTable.value = new Map()
}

const replaceImageItem = (
  imageMapRef: typeof remarkImagesByMainTable | typeof optionExampleImagesByMainTable,
  mainTableId: string,
  fileName: string,
  patch: Partial<DetailImageItem>
) => {
  const currentMap = new Map(imageMapRef.value)
  const currentImages = currentMap.get(mainTableId) || []
  const nextImages = currentImages.map((image) => {
    if (image.fileName !== fileName) return image
    if (image.url?.startsWith('blob:') && patch.url && patch.url !== image.url) {
      URL.revokeObjectURL(image.url)
    }
    return { ...image, ...patch }
  })
  currentMap.set(mainTableId, nextImages)
  imageMapRef.value = currentMap
}

// 加载备注图像
const loadRemarkImages = (imagesByMainTable: Map<string, RemarkImageSource[]>): Promise<void> => {
  if (!imagesByMainTable || imagesByMainTable.size === 0) {
    clearRemarkImages()
    remarkImagesLoadingPromise = Promise.resolve()
    return remarkImagesLoadingPromise
  }

  clearRemarkImages()
  const resultMap = new Map<string, DetailImageItem[]>()
  const tasks: Promise<void>[] = []
  
  for (const [mainTableId, imageList] of imagesByMainTable.entries()) {
    const images = imageList.map((image) => ({
      fileName: image.fileName,
      remark: image.remark,
      url: '',
      loading: true,
      error: false
    }))

    if (images.length > 0) {
      resultMap.set(mainTableId, images)
    }

    imageList.forEach(({ fileName }) => {
      tasks.push(
        list.downloadPhoto({ 文件名: fileName })
          .then((blob) => {
            const url = URL.createObjectURL(blob)
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url,
              loading: false,
              error: false
            })
          })
          .catch((error) => {
            console.error(`加载图片失败: ${fileName}`, error)
            replaceImageItem(remarkImagesByMainTable, mainTableId, fileName, {
              url: '',
              loading: false,
              error: true
            })
          })
      )
    })
  }

  remarkImagesByMainTable.value = resultMap
  remarkImagesLoadingPromise = Promise.allSettled(tasks).then(() => undefined)
  return remarkImagesLoadingPromise
}

const loadOptionExampleImages = (imagesByMainTable: Map<string, string[]>): Promise<void> => {
  if (!imagesByMainTable || imagesByMainTable.size === 0) {
    clearOptionExampleImages()
    return Promise.resolve()
  }

  clearOptionExampleImages()
  const resultMap = new Map<string, DetailImageItem[]>()
  const tasks: Promise<void>[] = []

  for (const [mainTableId, imageList] of imagesByMainTable.entries()) {
    const images = imageList.map((fileName) => ({
      fileName,
      url: '',
      loading: true,
      error: false
    }))

    if (images.length > 0) {
      resultMap.set(mainTableId, images)
    }

    imageList.forEach((fileName) => {
      tasks.push(
        ParameterSettings.clientDownload({ fileName })
          .then((res) => {
            const url = parseDownloadResponseToUrl(res)
            if (!url) throw new Error('图片内容为空')
            replaceImageItem(optionExampleImagesByMainTable, mainTableId, fileName, {
              url,
              loading: false,
              error: false
            })
          })
          .catch((error) => {
            console.error(`加载选项示意图失败: ${fileName}`, error)
            replaceImageItem(optionExampleImagesByMainTable, mainTableId, fileName, {
              url: '',
              loading: false,
              error: true
            })
          })
      )
    })
  }

  optionExampleImagesByMainTable.value = resultMap
  return Promise.allSettled(tasks).then(() => undefined)
}

const addUniqueImageFile = (imageList: string[], fileName: string) => {
  const normalized = toNonEmptyString(fileName)
  if (normalized && !imageList.includes(normalized)) {
    imageList.push(normalized)
  }
}

const parseOptionWithImage = (option: unknown): { label: string; fileName: string } | null => {
  const optionText = toNonEmptyString(option)
  if (!optionText) return null

  const [label, fileName] = optionText.split('|||')
  const normalizedLabel = toNonEmptyString(label)
  const normalizedFileName = toNonEmptyString(fileName)

  if (!normalizedLabel || !normalizedFileName) return null
  return { label: normalizedLabel, fileName: normalizedFileName }
}

const splitSelectedOptionValues = (value: unknown): string[] => {
  const selectedValue = toNonEmptyString(value)
  if (!selectedValue) return []

  return selectedValue
    .split(/[,，;；、|]/)
    .map((item) => toNonEmptyString(item))
    .filter(Boolean)
}

const collectSelectedOptionImages = (project: any, mainTable: any): string[] => {
  const formatDesc = project?.检测原始数据检测数据格式描述
  const optionColumns = Array.isArray(formatDesc?.选项) ? formatDesc.选项 : []
  const subRows = Array.isArray(mainTable?.检测原始数据详细数据子表集合)
    ? mainTable.检测原始数据详细数据子表集合
    : []

  if (optionColumns.length === 0 || subRows.length === 0) return []

  const selectedImages: string[] = []

  optionColumns.forEach((options: unknown, columnIndex: number) => {
    if (!Array.isArray(options) || options.length === 0) return

    const optionImageMap = new Map<string, string>()
    options.forEach((option) => {
      const parsed = parseOptionWithImage(option)
      if (parsed) {
        optionImageMap.set(parsed.label, parsed.fileName)
      }
    })

    if (optionImageMap.size === 0) return

    const fieldKey = `_${columnIndex + 1}`
    subRows.forEach((row: any) => {
      splitSelectedOptionValues(row?.[fieldKey]).forEach((selectedValue) => {
        const fileName = optionImageMap.get(selectedValue)
        if (fileName) {
          addUniqueImageFile(selectedImages, fileName)
        }
      })
    })
  })

  return selectedImages
}

// 加载汇总数据
const loadSummaryData = async () => {
  const id = route.query.id as string
  
  if (!id) {
    ElMessage.error('缺少经营项目ID参数')
    return
  }

  loading.value = true
  try {
    // 调用汇总接口（传入经营项目ID）
    const res = await ParameterSettings.getAllRawDataSummary({ id })

    if (res && (res.status === 200 || res.status === 0) && res.success && res.response) {
      summaryData.value = res.response as Record<string, unknown>
      
      // 从检测原始数据检测项目集合中查找备注图像列表和选项示意图（按主表分组）
      const imagesByMainTable = new Map<string, RemarkImageSource[]>()
      const optionExampleImagesByMainTable = new Map<string, string[]>()
      
      if (res.response['检测原始数据检测项目集合'] && Array.isArray(res.response['检测原始数据检测项目集合'])) {
        const projects = res.response['检测原始数据检测项目集合'] as any[]
        
        // 遍历所有检测项目
        for (const project of projects) {
          if (project['检测原始数据详细数据主表集合'] && Array.isArray(project['检测原始数据详细数据主表集合'])) {
            const mainTables = project['检测原始数据详细数据主表集合'] as any[]
            
            // 遍历所有主表，收集备注图像列表
            for (const mainTable of mainTables) {
              const mainTableId = String(mainTable.Id || mainTable.id)
              let imageList: RemarkImageSource[] = []
              const optionExampleImage = toNonEmptyString(mainTable['选项示意图'] ?? mainTable['选项示意'])
              const optionImageList: string[] = []
              
              if (mainTable['备注图像列表'] && Array.isArray(mainTable['备注图像列表'])) {
                imageList = parseRemarkImages(mainTable['备注图像列表'])
              } else if (mainTable['备注图像列表json']) {
                try {
                  const parsed = JSON.parse(mainTable['备注图像列表json'])
                  if (Array.isArray(parsed)) {
                    imageList = parseRemarkImages(parsed)
                  }
                } catch (e) {
                  console.error('解析备注图像列表json失败:', e)
                }
              }
              
              if (imageList.length > 0) {
                imagesByMainTable.set(mainTableId, imageList)
              }

              if (optionExampleImage) {
                addUniqueImageFile(optionImageList, optionExampleImage)
              }

              collectSelectedOptionImages(project, mainTable).forEach((fileName) => {
                addUniqueImageFile(optionImageList, fileName)
              })

              if (optionImageList.length > 0) {
                optionExampleImagesByMainTable.set(mainTableId, optionImageList)
              }
            }
          }
        }
      }
      
      if (imagesByMainTable.size > 0) {
        remarkImagesLoadingPromise = loadRemarkImages(imagesByMainTable)
      } else {
        clearRemarkImages()
        remarkImagesLoadingPromise = Promise.resolve()
      }

      void loadOptionExampleImages(optionExampleImagesByMainTable)
       
      ElMessage.success('数据加载成功')
    } else {
      ElMessage.error(res?.msg || '加载汇总数据失败')
    }
  } catch (error) {
    console.error('加载汇总数据失败:', error)
    ElMessage.error('加载汇总数据失败')
  } finally {
    loading.value = false
  }
}

// 返回上一页
const goBack = () => {
  router.go(-1)
}

const getExcelColumnLetter = (columnNumber: number): string => {
  let result = ''
  let value = columnNumber
  while (value > 0) {
    const remainder = (value - 1) % 26
    result = String.fromCharCode(65 + remainder) + result
    value = Math.floor((value - 1) / 26)
  }
  return result
}

// 下载Excel图片包
const handleDownloadExcelImages = async () => {
  if (!summaryData.value) {
    ElMessage.warning('暂无汇总数据')
    return
  }

  const projects = summaryData.value['检测原始数据检测项目集合'] as any[]
  if (!projects || projects.length === 0) {
    ElMessage.warning('暂无检测项目数据')
    return
  }

  const imageItems = Array.from(remarkImagesByMainTable.value.values()).flat()
  if (imageItems.some((image) => image.loading)) {
    ElMessage.info('现场图片仍在加载，已等待全部图片加载完成后继续下载')
  }

  downloadingImages.value = true
  try {
    await remarkImagesLoadingPromise
  } catch (error) {
    console.error('等待现场图片加载失败:', error)
    ElMessage.error('现场图片加载失败，无法下载完整图片包')
    downloadingImages.value = false
    return
  }

  const failedImages = Array.from(remarkImagesByMainTable.value.values())
    .flat()
    .filter((image) => image.error || !image.url)
  if (failedImages.length > 0) {
    ElMessage.warning(`有 ${failedImages.length} 张现场图片加载失败，已停止下载，请刷新后重试`)
    downloadingImages.value = false
    return
  }

  // 收集所有需要下载的图片（按显示摘要分组）
  const imageGroups: Array<{ summary: string; images: DetailImageItem[] }> = []
  
  for (const project of projects) {
    const mainTables = project['检测原始数据详细数据主表集合'] as any[]
    if (!mainTables || mainTables.length === 0) continue
    
    for (const mainData of mainTables) {
      const mainTableId = String(mainData.Id || mainData.id)
      const summary = mainData['显示摘要'] || '未命名数据表'
      const images = remarkImagesByMainTable.value.get(mainTableId)
      
      if (images && images.length > 0) {
        // 过滤掉加载失败的图片
        const validImages = images.filter(img => !img.error && !img.loading && img.url)
        if (validImages.length > 0) {
          imageGroups.push({
            summary,
            images: validImages
          })
        }
      }
    }
  }

  if (imageGroups.length === 0) {
    ElMessage.warning('暂无现场照片可下载')
    downloadingImages.value = false
    return
  }

  try {
    ElMessage.info('正在生成Excel文件，请稍候...')

    const [excelJsModule, fileSaverModule] = await Promise.all([
      import('exceljs'),
      import('file-saver')
    ])

    const ExcelJS = excelJsModule.default || excelJsModule
    const saveAs = fileSaverModule.saveAs || fileSaverModule.default?.saveAs || fileSaverModule.default
    if (typeof saveAs !== 'function') {
      throw new Error('file-saver 加载失败')
    }

    // 创建工作簿
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('现场照片')

    // A 列为检测摘要，后续按实际照片数量动态创建“照片 + 备注”列。
    worksheet.getColumn(1).width = 80
    const maxImageCount = Math.max(...imageGroups.map(group => group.images.length))
    for (let imageIndex = 0; imageIndex < maxImageCount; imageIndex++) {
      worksheet.getColumn(imageIndex * 2 + 2).width = 18
      worksheet.getColumn(imageIndex * 2 + 3).width = 26
    }

    let currentRow = 1

    // 遍历每个分组
    for (const group of imageGroups) {
      // 添加显示摘要标题
      const titleCell = worksheet.getCell(`A${currentRow}`)
      titleCell.value = group.summary
      titleCell.font = { bold: true, size: 12 }
      titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
      
      // 设置行高
      worksheet.getRow(currentRow).height = 90

      // 添加图片及其右侧备注，图片数量不设上限。
      for (let i = 0; i < group.images.length; i++) {
        const image = group.images[i]
        const imageColumnLetter = getExcelColumnLetter(i * 2 + 2)
        const remarkColumnLetter = getExcelColumnLetter(i * 2 + 3)

        const remark = image.remark?.trim()
        const remarkCell = worksheet.getCell(`${remarkColumnLetter}${currentRow}`)
        remarkCell.value = `备注：${remark || '无'}`
        remarkCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }

        try {
          // 将blob URL转换为ArrayBuffer
          const response = await fetch(image.url)
          const blob = await response.blob()
          const arrayBuffer = await blob.arrayBuffer()
          
          // 添加图片到工作簿
          const imageId = workbook.addImage({
            buffer: arrayBuffer,
            extension: 'jpeg'
          })

          // 使用单元格引用方式插入图片（确保图片在指定列内）
          worksheet.addImage(imageId, `${imageColumnLetter}${currentRow}:${imageColumnLetter}${currentRow}`)
        } catch (error) {
          console.error(`添加图片失败: ${image.fileName}`, error)
        }
      }

      currentRow++
    }

    // 生成Excel文件
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })
    
    // 下载文件
    const fileName = `${businessInfo.value?.name || '原始数据汇总'}_现场照片_${new Date().getTime()}.xlsx`
    saveAs(blob, fileName)
    
    ElMessage.success('Excel图片包下载成功')
  } catch (error) {
    console.error('下载Excel图片包失败:', error)
    ElMessage.error('下载Excel图片包失败')
  } finally {
    downloadingImages.value = false
  }
}

onMounted(() => {
  loadSummaryData()
})

onBeforeUnmount(() => {
  clearRemarkImages()
  clearOptionExampleImages()
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_image = _resolveComponent("el-image")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PageHeader, {
      size: "page",
      divider: ""
    }, {
      title: _withCtx(() => [
        _createVNode(_component_el_button, {
          type: "primary",
          icon: _unref(ArrowLeft),
          onClick: goBack,
          class: "back-button"
        }, {
          default: _withCtx(() => _cache[3] || (_cache[3] = [
            _createTextVNode(" 返回 ")
          ])),
          _: 1
        }, 8, ["icon"]),
        _createElementVNode("span", null, _toDisplayString(_unref(pageTitle)), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_component_el_button, {
          type: "success",
          icon: _unref(Download),
          onClick: handleDownloadExcelImages,
          loading: downloadingImages.value,
          disabled: !summaryData.value
        }, {
          default: _withCtx(() => _cache[4] || (_cache[4] = [
            _createTextVNode(" 下载Excel图片包 ")
          ])),
          _: 1
        }, 8, ["icon", "loading", "disabled"])
      ]),
      _: 1
    }),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_el_skeleton, {
            rows: 5,
            animated: ""
          })
        ]))
      : (summaryData.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_el_card, {
              class: "info-card",
              shadow: "hover"
            }, {
              header: _withCtx(() => [
                _createVNode(PageHeader, { size: "card" }, {
                  title: _withCtx(() => _cache[5] || (_cache[5] = [
                    _createTextVNode("基本信息")
                  ])),
                  _: 1
                })
              ]),
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_component_el_tag, {
                      type: "success",
                      size: "large",
                      class: "info-tag"
                    }, {
                      default: _withCtx(() => [
                        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "tag-label" }, "名称：", -1)),
                        _createElementVNode("span", _hoisted_6, _toDisplayString(businessInfo.value?.name || '未知项目'), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_tag, {
                      type: "info",
                      size: "large",
                      class: "info-tag"
                    }, {
                      default: _withCtx(() => [
                        _cache[7] || (_cache[7] = _createElementVNode("span", { class: "tag-label" }, "编码：", -1)),
                        _createElementVNode("span", _hoisted_7, _toDisplayString(businessInfo.value?.code || '无编码'), 1)
                      ]),
                      _: 1
                    })
                  ])
                ])
              ]),
              _: 1
            }),
            ((summaryData.value.检测原始数据检测项目集合 as any[])?.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((summaryData.value.检测原始数据检测项目集合 as any[]), (project, projectIndex) => {
                    return (_openBlock(), _createBlock(_component_el_card, {
                      key: (project as any).Id,
                      class: "project-card",
                      shadow: "hover"
                    }, {
                      header: _withCtx(() => [
                        _createVNode(PageHeader, { size: "card" }, {
                          title: _withCtx(() => [
                            _createTextVNode(_toDisplayString((project as any).名称), 1)
                          ]),
                          actions: _withCtx(() => [
                            _createVNode(_component_el_tag, {
                              type: "info",
                              size: "small"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode("项目 " + _toDisplayString(Number(projectIndex) + 1), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      default: _withCtx(() => [
                        (((project as any).检测原始数据检测项目特殊属性集合 as any[])?.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                              _createElementVNode("div", _hoisted_10, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(((project as any).检测原始数据检测项目特殊属性集合 as any[]), (attr) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: (attr as any).标题,
                                    class: "attribute-item"
                                  }, [
                                    _createElementVNode("span", _hoisted_11, _toDisplayString((attr as any).标题) + "：", 1),
                                    _createElementVNode("span", _hoisted_12, _toDisplayString((attr as any).内容 || '无'), 1)
                                  ]))
                                }), 128))
                              ])
                            ]))
                          : _createCommentVNode("", true),
                        (((project as any).检测原始数据详细数据主表集合 as any[])?.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                              _cache[13] || (_cache[13] = _createElementVNode("h4", null, "详细数据", -1)),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(((project as any).检测原始数据详细数据主表集合 as any[]), (mainData, mainIndex) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: (mainData as any).Id,
                                  class: "data-table-container"
                                }, [
                                  _createElementVNode("div", _hoisted_14, [
                                    _createElementVNode("span", _hoisted_15, "数据表 " + _toDisplayString(Number(mainIndex) + 1), 1),
                                    _createElementVNode("span", _hoisted_16, _toDisplayString((mainData as any).显示摘要), 1),
                                    (getOptionExampleImages(mainData).length > 0)
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getOptionExampleImages(mainData), (optionImage, optionImageIndex) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                              key: `${optionImage.fileName}_${optionImageIndex}`,
                                              class: "option-example-image"
                                            }, [
                                              (optionImage.loading)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                                    _createVNode(_component_el_icon, { class: "is-loading" }, {
                                                      default: _withCtx(() => [
                                                        _createVNode(_unref(Loading))
                                                      ]),
                                                      _: 1
                                                    }),
                                                    _cache[8] || (_cache[8] = _createElementVNode("span", null, "加载中", -1))
                                                  ]))
                                                : (optionImage.error)
                                                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                                      _createVNode(_component_el_icon, null, {
                                                        default: _withCtx(() => [
                                                          _createVNode(_unref(Picture))
                                                        ]),
                                                        _: 1
                                                      }),
                                                      _cache[9] || (_cache[9] = _createElementVNode("span", null, "加载失败", -1))
                                                    ]))
                                                  : (_openBlock(), _createBlock(_component_el_image, {
                                                      key: 2,
                                                      src: optionImage.url,
                                                      fit: "contain",
                                                      class: "option-example-thumb",
                                                      "preview-src-list": getOptionExampleImages(mainData).filter(img => !img.error && !img.loading && img.url).map(img => img.url),
                                                      "initial-index": getOptionExampleImages(mainData).filter(img => !img.error && !img.loading && img.url).findIndex(img => img.url === optionImage.url),
                                                      "preview-teleported": true,
                                                      "hide-on-click-modal": true
                                                    }, {
                                                      placeholder: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_20, [
                                                          _createVNode(_component_el_icon, { class: "is-loading" }, {
                                                            default: _withCtx(() => [
                                                              _createVNode(_unref(Loading))
                                                            ]),
                                                            _: 1
                                                          })
                                                        ])
                                                      ]),
                                                      error: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_21, [
                                                          _createVNode(_component_el_icon, null, {
                                                            default: _withCtx(() => [
                                                              _createVNode(_unref(Picture))
                                                            ]),
                                                            _: 1
                                                          })
                                                        ])
                                                      ]),
                                                      _: 2
                                                    }, 1032, ["src", "preview-src-list", "initial-index"]))
                                            ]))
                                          }), 128))
                                        ]))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createVNode(_component_el_table, {
                                    data: (mainData as any).检测原始数据详细数据子表集合 as any[],
                                    border: "",
                                    size: "small",
                                    class: "data-table",
                                    "scrollbar-always-on": ""
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_el_table_column, {
                                        prop: "Header",
                                        label: "行号",
                                        width: "80"
                                      }),
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(((project as any).检测原始数据检测数据格式描述?.横向标题 as string[]), (title, index) => {
                                        return (_openBlock(), _createBlock(_component_el_table_column, {
                                          key: index,
                                          prop: `_${Number(index) + 1}`,
                                          label: getTransformedTitle(title, project as any, mainData as any),
                                          width: 120,
                                          "show-overflow-tooltip": "",
                                          "class-name": hasDividerLine(project as any, Number(index)) ? 'divider-column' : ''
                                        }, null, 8, ["prop", "label", "class-name"]))
                                      }), 128))
                                    ]),
                                    _: 2
                                  }, 1032, ["data"]),
                                  (getMainTableImages(mainData).length > 0)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                        _cache[12] || (_cache[12] = _createElementVNode("h5", null, "现场照片", -1)),
                                        _createElementVNode("div", _hoisted_23, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getMainTableImages(mainData), (image, imgIndex) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                              key: imgIndex,
                                              class: "image-item-small"
                                            }, [
                                              (image.loading)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                                    _createVNode(_component_el_icon, { class: "is-loading" }, {
                                                      default: _withCtx(() => [
                                                        _createVNode(_unref(Loading))
                                                      ]),
                                                      _: 1
                                                    }),
                                                    _cache[10] || (_cache[10] = _createElementVNode("span", null, "加载中", -1))
                                                  ]))
                                                : (image.error)
                                                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                                                      _createVNode(_component_el_icon, null, {
                                                        default: _withCtx(() => [
                                                          _createVNode(_unref(Picture))
                                                        ]),
                                                        _: 1
                                                      }),
                                                      _cache[11] || (_cache[11] = _createElementVNode("span", null, "加载失败", -1))
                                                    ]))
                                                  : (_openBlock(), _createBlock(_component_el_image, {
                                                      key: 2,
                                                      src: image.url,
                                                      fit: "cover",
                                                      class: "thumbnail-image-small",
                                                      onClick: ($event: any) => (openRemarkImagePreview(mainData, imgIndex))
                                                    }, {
                                                      error: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_26, [
                                                          _createVNode(_component_el_icon, null, {
                                                            default: _withCtx(() => [
                                                              _createVNode(_unref(Picture))
                                                            ]),
                                                            _: 1
                                                          })
                                                        ])
                                                      ]),
                                                      placeholder: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_27, [
                                                          _createVNode(_component_el_icon, { class: "is-loading" }, {
                                                            default: _withCtx(() => [
                                                              _createVNode(_unref(Loading))
                                                            ]),
                                                            _: 1
                                                          })
                                                        ])
                                                      ]),
                                                      _: 2
                                                    }, 1032, ["src", "onClick"]))
                                            ]))
                                          }), 128))
                                        ])
                                      ]))
                                    : _createCommentVNode("", true)
                                ]))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]))
              : (_openBlock(), _createBlock(_component_el_empty, {
                  key: 1,
                  description: "暂无检测项目数据"
                }))
          ]))
        : (_openBlock(), _createBlock(_component_el_empty, {
            key: 2,
            description: "暂无汇总数据"
          })),
    _createVNode(_component_el_dialog, {
      modelValue: remarkImagePreviewVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((remarkImagePreviewVisible).value = $event)),
      title: "现场照片",
      width: "min(860px, 92vw)",
      top: "6vh",
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("div", _hoisted_29, [
            (remarkPreviewImages.value.length > 1)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  class: "remark-preview-nav remark-preview-nav--previous",
                  icon: _unref(ArrowLeft),
                  circle: "",
                  disabled: remarkPreviewIndex.value === 0,
                  "aria-label": "上一张",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (changeRemarkPreviewImage(-1)))
                }, null, 8, ["icon", "disabled"]))
              : _createCommentVNode("", true),
            (activeRemarkPreviewImage.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 1,
                  src: activeRemarkPreviewImage.value.url,
                  class: "remark-image-preview-image",
                  alt: "现场照片"
                }, null, 8, _hoisted_30))
              : _createCommentVNode("", true),
            (remarkPreviewImages.value.length > 1)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 2,
                  class: "remark-preview-nav remark-preview-nav--next",
                  icon: _unref(ArrowRight),
                  circle: "",
                  disabled: remarkPreviewIndex.value >= remarkPreviewImages.value.length - 1,
                  "aria-label": "下一张",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (changeRemarkPreviewImage(1)))
                }, null, 8, ["icon", "disabled"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_31, [
            _cache[14] || (_cache[14] = _createElementVNode("span", { class: "remark-image-preview-note-label" }, "备注", -1)),
            _createElementVNode("span", _hoisted_32, _toDisplayString(activeRemarkPreviewImage.value?.remark || '无'), 1)
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})